import { useRef, useMemo } from 'react';
import JoditEditor from 'jodit-react';
import { Box } from '@mui/material';

interface JoditEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  error?: boolean;
  height?: number;
}

const JoditEditorComponent = ({
  value,
  onChange,
  placeholder = 'Enter content here...',
  error = false,
  height = 400,
}: JoditEditorProps) => {
  const editor = useRef(null);

  const config = useMemo(
    () =>
      ({
        readonly: false,
        placeholder,
        height,
        toolbar: true,
        spellcheck: true,
        language: 'en',
        toolbarButtonSize: 'middle' as const,
        toolbarAdaptive: false,
        showCharsCounter: true,
        showWordsCounter: true,
        showXPathInStatusbar: false,
        defaultActionOnPaste: 'insert_clear' as const,
        pastePlainText: true, // 👈 force clean paste
        askBeforePasteFromWord: false,
        askBeforePasteHTML: false,
        disablePlugins: ['paste', 'pasteFromWord'],
        cleanHTML: {
          cleanOnPaste: true,
          removeEmptyElements: true,
          replaceNBSP: true,
        },
        buttons: [
          'source',
          '|',
          'bold',
          'strikethrough',
          'underline',
          'italic',
          '|',
          'superscript',
          'subscript',
          '|',
          'align',
          '|',
          'ul',
          'ol',
          '|',
          'outdent',
          'indent',
          '|',
          'font',
          'fontsize',
          'brush',
          'paragraph',
          '|',
          'image',
          'link',
          'table',
          '|',
          'hr',
          'eraser',
          'copyformat',
          '|',
          'fullsize',
          'selectall',
          'print',
          '|',
          'undo',
          'redo',
          '|',
          'cut',
          'copy',
          'paste',
          'pasteText',
          '|',
          'about',
        ],
        uploader: {
          insertImageAsBase64URI: true,
        },
        removeButtons: [],
        showPlaceholder: true,
      }) as any,
    [placeholder, height]
  );

  return (
    <Box>
      <Box
        sx={{
          border: error ? '2px solid #d32f2f' : '1px solid rgba(0, 0, 0, 0.23)',
          borderRadius: '4px',
          overflow: 'hidden',
          '&:hover': {
            borderColor: error ? '#d32f2f' : 'rgba(0, 0, 0, 0.87)',
          },
          '&:focus-within': {
            borderColor: error ? '#d32f2f' : '#1976d2',
            borderWidth: '2px',
          },
          '& .jodit-container': {
            borderRadius: '4px',
          },
          '& .jodit-wysiwyg': {
            minHeight: `${height}px`,
          },
        }}
      >
        <JoditEditor
          ref={editor}
          value={value || ''}
          config={config}
          onBlur={(newContent: string) => onChange(newContent)}
          onChange={(newContent: string) => onChange(newContent)}
        />
      </Box>
    </Box>
  );
};

export default JoditEditorComponent;
